import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getAgencies, addAgency, ameexConnect } from "../../../api/auth";
import { motion } from "framer-motion";
import ServiceUnavailable from "../../component/ServiceUnavailable";

const deliveryCompanies = [
  {
    code: "AMEEX",
    form: [
      { name: "connection_name", type: "text", label: "Connection Name" },
      { name: "api_id", type: "text", label: "AMEEX API Id (C-Api-Id)" },
      { name: "api_key", type: "text", label: "AMEEX API Key (clé live, ou clé test_ pour le sandbox)" },
      { name: "webhook_secret", type: "text", label: "Secret webhook HMAC (optionnel, recommandé)" },
    ],
  },
  {
    code: "OZON_EXPRESS",
    form: [
      { name: "connection_name", type: "text", label: "Connection Name" },
      { name: "client_id", type: "text", label: "Ozon Client ID" },
      { name: "api_key", type: "text", label: "Ozon API Key" },
    ],
  },
  {
    code: "ONESTA",
    form: [
      { name: "connection_name", type: "text", label: "Connection Name" },
      { name: "client_id", type: "text", label: "Onessta Client ID" },
      { name: "api_key", type: "text", label: "Onessta API Key" },
    ],
  },
  {
    code: "SENDIT",
    form: [
      { name: "connection_name", type: "text", label: "Connection Name" },
      { name: "client_id", type: "text", label: "Sendit Clé publique" },
      { name: "api_key", type: "text", label: "Sendit Clé privé" },
      { name: "pickup_district_id", type: "number", label: "Id du quartier de ramassage (Sendit)" },
    ],
  },
  {
    code: "FORCELOG",
    form: [
      { name: "connection_name", type: "text", label: "Connection Name" },
      { name: "api_key", type: "text", label: "ForceLog API Key" },
    ],
  },
  {
    code: "OLIVRAISON",
    form: [
      { name: "connection_name", type: "text", label: "Connection Name" },
      { name: "client_id", type: "text", label: "Olivraison apiKey" },
      { name: "api_key", type: "text", label: "Olivraison secretKey" },
    ],
  },
  {
    code: "CATHEDIS",
    form: [
      { name: "connection_name", type: "text", label: "Connection Name" },
      { name: "client_id", type: "text", label: "Cathedis username" },
      { name: "api_key", type: "text", label: "Cathedis password" },
    ],
  },
];

export default function DeliveryConnect() {
  const { slug } = useParams();
  const cleanSlug = slug.replace(":", "").toLowerCase();
  const navigate = useNavigate();

  const [companyForm, setCompanyForm] = useState([]);
  const [agency, setAgency] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [globalError, setGlobalError] = useState("");
  const [success, setSuccess] = useState("");
  const [loadError, setLoadError] = useState(false);
  const [ameexConnection, setAmeexConnection] = useState(null);
  const [copied, setCopied] = useState(false);

  const loadAgency = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const agencies = await getAgencies();

      const found = agencies.find(
        (a) => a.code?.toLowerCase() === cleanSlug
      );

      if (found) {
        setAgency(found);

        const company = deliveryCompanies.find(
          (c) => c.code.toLowerCase() === found.code.toLowerCase()
        );

        if (company) {
          setCompanyForm(company.form);

          const initial = { is_active: true };

          company.form.forEach((f) => {
            initial[f.name] = "";
          });

          setForm(initial);
        }
      }
    } catch (err) {
      console.error(err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAgency();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleanSlug]);

  const handleChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [key]: null,
    }));
  };

  const handleConnect = async () => {
    if (!agency) return;

    try {
      setSaving(true);
      setErrors({});
      setGlobalError("");
      setSuccess("");

      if (agency.code?.toUpperCase() === "AMEEX") {
        // Identifiants testés chez AMEEX avant l'enregistrement
        const conn = await ameexConnect({
          connection_name: form.connection_name,
          api_id: form.api_id,
          api_key: form.api_key,
          ...(form.webhook_secret ? { webhook_secret: form.webhook_secret } : {}),
        });
        setAmeexConnection(conn);
        setSuccess(
          conn.mode === "test"
            ? "Compte AMEEX connecté en mode SANDBOX (clé test_)."
            : "Compte AMEEX connecté."
        );
        return; // l'utilisateur doit d'abord copier l'URL webhook
      }

      // Sendit : le quartier de ramassage est lu par le backend dans extra_config
      const { pickup_district_id, ...fields } = form;
      const payload = {
        agency: agency.id,
        ...fields,
        is_active: true,
        ...(pickup_district_id ? { extra_config: { pickup_district_id: Number(pickup_district_id) } } : {}),
      };

      await addAgency(payload);

      setSuccess("Connection created successfully.");

      setTimeout(() => {
        navigate("/delivery/dashboard");
      }, 1200);
    } catch (err) {
      if (err.response?.data) {
        const data = err.response.data;

        const fieldErrors = {};
        Object.keys(data).forEach((key) => {
          if (Array.isArray(data[key])) {
            fieldErrors[key] = data[key][0];
          }
        });

        setErrors(fieldErrors);

        if (data.non_field_errors) {
          setGlobalError(data.non_field_errors[0]);
        } else if (typeof data.detail === "string") {
          setGlobalError(data.detail);
        }
      } else {
        setGlobalError("Something went wrong.");
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-10 text-center text-gray-500 dark:bg-slate-950 dark:text-slate-500">
        Loading agency...
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="p-10 dark:bg-slate-950">
        <div className="max-w-xl mx-auto">
          <ServiceUnavailable label="Le service de livraison" onRetry={loadAgency} />
        </div>
      </div>
    );
  }

  if (!agency) {
    return (
      <div className="p-10 text-center text-red-500 dark:bg-slate-950 dark:text-rose-400">
        Agency not found
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8 dark:bg-slate-950">
      <div className="max-w-xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-semibold dark:text-slate-100">Connect {agency.name}</h1>
          <p className="text-gray-500 text-sm dark:text-slate-500">
            Add your delivery credentials to enable shipments.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border rounded-2xl p-6 space-y-4 shadow-sm dark:bg-slate-900 dark:border-slate-800"
        >
          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 text-sm p-3 rounded-lg dark:bg-emerald-500/10 dark:border-emerald-500/30 dark:text-emerald-400">
              {success}
            </div>
          )}

          {globalError && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-lg dark:bg-rose-500/10 dark:border-rose-500/30 dark:text-rose-400">
              {globalError}
            </div>
          )}

          {ameexConnection?.webhook_url && (
            <div className="border border-indigo-200 bg-indigo-50 rounded-lg p-3 space-y-2 text-sm dark:bg-indigo-500/10 dark:border-indigo-500/30">
              <p className="font-medium dark:text-slate-100">
                URL webhook à coller dans AMEEX
                {ameexConnection.mode === "test"
                  ? " (Développeur / API > Sandbox > URL du webhook de test)"
                  : " (Mes businesses > Modifier > API)"}
              </p>
              <div className="flex gap-2">
                <input
                  readOnly
                  value={ameexConnection.webhook_url}
                  className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-xs bg-white dark:bg-slate-800 dark:text-slate-100 dark:border-slate-700"
                />
                <button
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(ameexConnection.webhook_url);
                      setCopied(true);
                    } catch {
                      setCopied(false);
                    }
                  }}
                  className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs"
                >
                  {copied ? "Copié" : "Copier"}
                </button>
              </div>
              <p className="text-xs text-gray-600 dark:text-slate-400">
                {ameexConnection.webhook_signature_enabled
                  ? "Signature HMAC activée : les webhooks non signés seront refusés."
                  : "Conseil : générez un secret webhook chez AMEEX et enregistrez-le ici pour sécuriser les webhooks."}
              </p>
              <div className="flex justify-end">
                <button
                  onClick={() => navigate("/delivery/dashboard")}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                >
                  Continuer
                </button>
              </div>
            </div>
          )}

          {!ameexConnection && companyForm.map((field) => (
            <div key={field.name} className="space-y-1">
              <label className="text-sm font-medium dark:text-slate-300">{field.label}</label>

              <input
                type={field.type || "text"}
                value={form[field.name] || ""}
                onChange={(e) => handleChange(field.name, e.target.value)}
                className={`w-full border rounded-lg px-3 py-2 text-sm dark:bg-slate-800 dark:text-slate-100 ${
                  errors[field.name] ? "border-red-400 dark:border-rose-500/50" : "border-gray-200 dark:border-slate-700"
                }`}
              />

              {errors[field.name] && (
                <p className="text-red-500 text-xs dark:text-rose-400">{errors[field.name]}</p>
              )}
            </div>
          ))}

          {!ameexConnection && (
          <div className="flex justify-end gap-3 pt-4">
            <button
              onClick={() => navigate("/delivery")}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
            >
              Cancel
            </button>

            <button
              onClick={handleConnect}
              disabled={saving}
              className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm disabled:opacity-60"
            >
              {saving ? "Connecting..." : "Connect"}
            </button>
          </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}